import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ViewManager } from '../../../../../core/view-manager';
import { LanguageProvider } from '../../../../../core/language-provider';
import { DEFAULT_FONT } from '../../utils/constants';
import SubFieldSelector from '../SubFieldSelector';
import './style.css';

export default function TableEditor({ element, availableFields, onUpdateElement }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [pendingExternalFieldIndex, setPendingExternalFieldIndex] = useState(null);
  const [pendingExternalField, setPendingExternalField] = useState(null);

  const columns = element.columns || [];

  // Helper to check if a field is an externalEntity
  const isExternalEntityField = (field) => {
    return field && ['externalEntity', 'deepExternalEntity'].includes(field.type) && field.options?.entity;
  };

  // Get sub-fields for an externalEntity field
  const getSubFields = (field) => {
    if (!isExternalEntityField(field)) return [];

    const entityName = field.options.entity;
    const entityProps = ViewManager.getEntityProperties(entityName);

    if (!entityProps?.columns) return [];

    return Object.entries(entityProps.columns)
      .filter(([name, config]) => {
        const excluded = ['deleted', 'password'];
        const excludedTypes = ['password', 'file', 'image', 'multipleExternalEntity'];
        return !excluded.includes(name) && !excludedTypes.includes(config.input_type);
      })
      .map(([name, config]) => ({
        name: name,
        label: LanguageProvider.getLabel(entityName, name) || name,
        type: config.input_type,
        isLabelField: name === field.options.labelField
      }))
      .sort((a, b) => {
        if (a.isLabelField) return -1;
        if (b.isLabelField) return 1;
        return a.label.localeCompare(b.label);
      });
  };

  // Add a new column
  const addColumn = (field = null) => {
    const newColumn = {
      id: 'col_' + Date.now(),
      field: field,
      width: 'auto',
      headerStyle: { ...DEFAULT_FONT, fontWeight: 'bold' },
      cellStyle: { ...DEFAULT_FONT }
    };

    onUpdateElement({
      columns: [...columns, newColumn]
    });
  };

  // Remove a column
  const removeColumn = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    onUpdateElement({ columns: newColumns });
  };

  // Update column field
  const updateColumnField = (index, fieldId) => {
    const field = availableFields.find(f => f.id === fieldId) || null;

    // Check if it's an externalEntity - need to select subField
    if (isExternalEntityField(field)) {
      setPendingExternalFieldIndex(index);
      setPendingExternalField(field);
      return;
    }

    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      field: field,
      subField: null // Clear subField if changing to non-external field
    };
    onUpdateElement({ columns: newColumns });
  };

  // Handle subField selection for externalEntity in table column
  const handleSubFieldSelect = (fieldWithSubField) => {
    if (pendingExternalFieldIndex === null) return;

    const newColumns = [...columns];
    newColumns[pendingExternalFieldIndex] = {
      ...newColumns[pendingExternalFieldIndex],
      field: fieldWithSubField
    };
    onUpdateElement({ columns: newColumns });

    setPendingExternalFieldIndex(null);
    setPendingExternalField(null);
  };

  // Cancel subField selection
  const handleSubFieldCancel = () => {
    setPendingExternalFieldIndex(null);
    setPendingExternalField(null);
  };

  // Update subField for existing external entity column
  const updateColumnSubField = (index, subFieldName) => {
    const col = columns[index];
    if (!col.field || !isExternalEntityField(col.field)) return;

    const subFields = getSubFields(col.field);
    const subField = subFields.find(sf => sf.name === subFieldName);

    if (subField) {
      const newColumns = [...columns];
      newColumns[index] = {
        ...newColumns[index],
        field: {
          ...col.field,
          subField: subField.name,
          subFieldLabel: subField.label,
          displayLabel: `${col.field.label} → ${subField.label}`
        }
      };
      onUpdateElement({ columns: newColumns });
    }
  };

  // Update column width
  const updateColumnWidth = (index, width) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: width
    };
    onUpdateElement({ columns: newColumns });
  };

  // Move column up
  const moveColumnUp = (index) => {
    if (index <= 0) return;
    const newColumns = [...columns];
    [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    onUpdateElement({ columns: newColumns });
  };

  // Move column down
  const moveColumnDown = (index) => {
    if (index >= columns.length - 1) return;
    const newColumns = [...columns];
    [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    onUpdateElement({ columns: newColumns });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    const newColumns = [...columns];
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, removed);

    onUpdateElement({ columns: newColumns });
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Update column header text (for text columns)
  const updateColumnHeader = (index, headerText) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      headerText: headerText
    };
    onUpdateElement({ columns: newColumns });
  };

  // Update column cell template
  const updateColumnCellTemplate = (index, cellTemplate) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      cellTemplate: cellTemplate
    };
    onUpdateElement({ columns: newColumns });
  };

  return (
    <div className="table-editor">
      <div className="table-editor-header">
        <span className="table-editor-title">
          <FontAwesomeIcon icon="table" /> Colonne Tabella
        </span>
        <span className="column-count">{columns.length} colonne</span>
      </div>

      {/* Add Column Button */}
      <div className="table-editor-actions">
        <button
          className="add-column-btn"
          onClick={() => addColumn()}
          title="Aggiungi una nuova colonna"
        >
          <FontAwesomeIcon icon="plus" /> Aggiungi Colonna
        </button>
      </div>

      {/* Column list */}
      <div className="column-list">
        {columns.length === 0 ? (
          <div className="no-columns">
            <FontAwesomeIcon icon="info-circle" />
            <p>Nessuna colonna. Usa il pulsante sopra o trascina dalla query.</p>
          </div>
        ) : (
          columns.map((col, index) => (
            <div
              key={col.id || index}
              className={`column-item ${draggedIndex === index ? 'dragging' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="column-drag-handle">
                <FontAwesomeIcon icon="grip-vertical" />
              </div>

              <div className="column-config">
                <div className="column-row">
                  <label>Campo:</label>
                  <select
                    value={col.field?.id || ''}
                    onChange={(e) => updateColumnField(index, e.target.value)}
                    className="column-field-select"
                  >
                    <option value="">-- Testo libero --</option>
                    {availableFields.map(field => (
                      <option key={field.id} value={field.id}>
                        {field.label}
                        {isExternalEntityField(field) ? ' (riferimento)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Show subField selector for externalEntity columns */}
                {col.field && isExternalEntityField(col.field) && (
                  <div className="column-row">
                    <label>Sotto-campo:</label>
                    <select
                      value={col.field.subField || ''}
                      onChange={(e) => updateColumnSubField(index, e.target.value)}
                      className="column-subfield-select"
                    >
                      <option value="">-- Seleziona --</option>
                      {getSubFields(col.field).map(sf => (
                        <option key={sf.name} value={sf.name}>
                          {sf.label} {sf.isLabelField ? '(predefinito)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Show external entity info badge */}
                {col.field && isExternalEntityField(col.field) && col.field.subField && (
                  <div className="column-row">
                    <label></label>
                    <div className="subfield-badge">
                      <FontAwesomeIcon icon="link" />
                      <span>{col.field.displayLabel || `${col.field.label} → ${col.field.subFieldLabel}`}</span>
                    </div>
                  </div>
                )}

                {/* Always show header text input */}
                <div className="column-row">
                  <label>Intestaz.:</label>
                  <input
                    type="text"
                    value={col.headerText !== undefined ? col.headerText : (col.field?.label || col.field?.name || '')}
                    onChange={(e) => updateColumnHeader(index, e.target.value)}
                    placeholder={col.field?.label || col.field?.name || 'Titolo colonna...'}
                    className="column-header-input"
                  />
                </div>

                {/* Cell template input */}
                <div className="column-row">
                  <label>Cella:</label>
                  <input
                    type="text"
                    value={col.cellTemplate || ''}
                    onChange={(e) => updateColumnCellTemplate(index, e.target.value)}
                    placeholder={col.field ? 'Es: Totale: {valore}' : 'Testo fisso...'}
                    className="column-cell-template-input"
                  />
                </div>
                {col.field && (
                  <div className="column-row">
                    <label></label>
                    <span className="column-hint">Usa {'{valore}'} per il campo</span>
                  </div>
                )}

                <div className="column-row">
                  <label>Larghezza:</label>
                  <select
                    value={col.width || 'auto'}
                    onChange={(e) => updateColumnWidth(index, e.target.value)}
                    className="column-width-select"
                  >
                    <option value="auto">Auto</option>
                    <option value="50px">50px</option>
                    <option value="75px">75px</option>
                    <option value="100px">100px</option>
                    <option value="125px">125px</option>
                    <option value="150px">150px</option>
                    <option value="200px">200px</option>
                    <option value="10%">10%</option>
                    <option value="15%">15%</option>
                    <option value="20%">20%</option>
                    <option value="25%">25%</option>
                    <option value="30%">30%</option>
                    <option value="50%">50%</option>
                  </select>
                </div>
              </div>

              <div className="column-actions">
                <button
                  className="column-action-btn"
                  onClick={() => moveColumnUp(index)}
                  disabled={index === 0}
                  title="Sposta su"
                >
                  <FontAwesomeIcon icon="chevron-up" />
                </button>
                <button
                  className="column-action-btn"
                  onClick={() => moveColumnDown(index)}
                  disabled={index === columns.length - 1}
                  title="Sposta giu'"
                >
                  <FontAwesomeIcon icon="chevron-down" />
                </button>
                <button
                  className="column-action-btn delete"
                  onClick={() => removeColumn(index)}
                  title="Rimuovi colonna"
                >
                  <FontAwesomeIcon icon="times" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info message */}
      <div className="table-editor-info">
        <FontAwesomeIcon icon="info-circle" />
        <span>Trascina le colonne dalla query per aggiungerle alla tabella</span>
      </div>

      {/* Clear table button */}
      {columns.length > 0 && (
        <div className="quick-actions">
          <button
            className="quick-action-btn danger"
            onClick={() => onUpdateElement({ columns: [] })}
            title="Rimuovi tutte le colonne"
          >
            <FontAwesomeIcon icon="trash" /> Svuota Tabella
          </button>
        </div>
      )}

      {/* SubFieldSelector for externalEntity fields */}
      <SubFieldSelector
        isOpen={pendingExternalField !== null}
        field={pendingExternalField}
        onSelect={handleSubFieldSelect}
        onCancel={handleSubFieldCancel}
      />
    </div>
  );
}
